/**
 * Страница создания нового заказа
 *
 * Реализует трёхшаговый wizard:
 * 1. Выбор источника заказа (Glovo, Wolt, звонок и т.д.)
 * 2. Заполнение полей формы (телефон, имя, промокод и т.д.)
 * 3. Выбор блюд из меню
 *
 * Архитектура:
 * - useAddOrder - хук для управления состоянием и логикой
 * - SourceSelectionStep - компонент первого шага
 * - FieldsFormStep - компонент второго шага
 * - DishesSelectionStep - компонент третьего шага
 * - OrderSummaryModal - модалка итогов заказа
 * - DishSelectionModal - модалка выбора порций блюда
 */
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { DishSelectionModal } from '../../components/DishSelectionModal';
import { useAddOrder } from './useAddOrder';
import {
  SourceSelectionStep,
  FieldsFormStep,
  DishesSelectionStep,
  OrderSummaryModal,
} from './components';

export const AddOrderPage: React.FC = () => {
  const { t } = useLanguage();

  // Получаем все состояния и методы из хука
  const {
    step,
    selectedSource,
    formData,
    employees,
    menuData,
    loadingMenu,
    selectedDishes,
    selectedDishForModal,
    isDishModalOpen,
    isOrderDetailsOpen,
    handleSourceSelect,
    handleBackToSource,
    handleBackToFields,
    handleFieldChange,
    loadMenu,
    handleDishClick,
    handleCloseDishModal,
    handleUpdateDishPortions,
    handleRemoveDish,
    handleUpdateQuantity,
    calculateFinalPrice,
    calculateTotal,
    handleCreateOrder,
    openOrderDetails,
    closeOrderDetails,
  } = useAddOrder();

  // ============ Шаг 1: Выбор источника ============
  if (step === 'source') {
    return <SourceSelectionStep onSourceSelect={handleSourceSelect} />;
  }

  // ============ Шаг 3: Выбор блюд ============
  if (step === 'dishes') {
    // Состояние загрузки
    if (loadingMenu) {
      return (
        <div className="min-h-screen bg-background-100 py-8 flex items-center justify-center">
          <div className="text-center">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-text-secondary">{t('addOrder.loadingMenu')}</p>
          </div>
        </div>
      );
    }

    // Меню не загружено
    if (!menuData) {
      return null;
    }

    return (
      <>
        <DishesSelectionStep
          selectedSource={selectedSource}
          menuData={menuData}
          selectedDishes={selectedDishes}
          onBack={handleBackToFields}
          onDishClick={handleDishClick}
          onRemoveDish={handleRemoveDish}
          onUpdateQuantity={handleUpdateQuantity}
          onOpenOrderDetails={openOrderDetails}
          calculateFinalPrice={calculateFinalPrice}
          calculateTotal={calculateTotal}
        />

        {/* Модальное окно выбора порций блюда */}
        <DishSelectionModal
          dish={selectedDishForModal}
          isOpen={isDishModalOpen}
          onClose={handleCloseDishModal}
          onUpdateDishPortions={handleUpdateDishPortions}
          promoInfo={menuData.promo}
          selectedPortions={
            selectedDishForModal
              ? selectedDishes
                  .filter((item) => item.dishId === selectedDishForModal.id)
                  .map((item) => ({
                    portionId: item.portionId,
                    quantity: item.quantity,
                    itemDiscountPercent: item.itemDiscountPercent,
                  }))
              : []
          }
        />

        {/* Модальное окно итогов заказа */}
        <OrderSummaryModal
          isOpen={isOrderDetailsOpen}
          onClose={closeOrderDetails}
          selectedDishes={selectedDishes}
          menuData={menuData}
          onRemoveDish={handleRemoveDish}
          onUpdateQuantity={handleUpdateQuantity}
          onCreateOrder={handleCreateOrder}
          calculateFinalPrice={calculateFinalPrice}
          calculateTotal={calculateTotal}
        />
      </>
    );
  }

  // ============ Шаг 2: Заполнение полей (по умолчанию) ============
  return (
    <FieldsFormStep
      selectedSource={selectedSource}
      formData={formData}
      employees={employees}
      onFieldChange={handleFieldChange}
      onBack={handleBackToSource}
      onSubmit={loadMenu}
      isLoading={loadingMenu}
    />
  );
};
